import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FilterIcon, GridIcon, ListIcon, ChevronDownIcon } from 'lucide-react';
import { products } from '../data/products';
import { ProductGrid } from '../components/products/ProductGrid';
import { ProductFilters } from '../components/products/ProductFilters';
import { Select } from '../components/ui/Select';
import { Button } from '../components/ui/Button';
const sortOptions = [{
  value: 'featured',
  label: 'Featured'
}, {
  value: 'newest',
  label: 'Newest First'
}, {
  value: 'price-low',
  label: 'Price: Low to High'
}, {
  value: 'price-high',
  label: 'Price: High to Low'
}, {
  value: 'rating',
  label: 'Highest Rated'
}, {
  value: 'popular',
  label: 'Most Popular'
}];
export function ProductListingPage() {
  const [searchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 200000]);
  const [sortBy, setSortBy] = useState('featured');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const filteredProducts = useMemo(() => {
    let result = [...products];
    // Category filter
    if (selectedCategory) {
      result = result.filter(p => p.categorySlug === selectedCategory);
    }
    // Brand filter
    if (selectedBrand) {
      result = result.filter(p => p.brand === selectedBrand);
    }
    // Price filter
    result = result.filter(p => p.price >= priceRange[0] && p.price <= priceRange[1]);
    // Sorting
    switch (sortBy) {
      case 'newest':
        result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      case 'price-low':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'popular':
        result.sort((a, b) => b.reviewCount - a.reviewCount);
        break;
    }
    return result;
  }, [selectedCategory, selectedBrand, priceRange, sortBy]);
  const clearFilters = () => {
    setSelectedCategory('');
    setSelectedBrand('');
    setPriceRange([0, 200000]);
  };
  const pageTitle = selectedCategory ? products.find(p => p.categorySlug === selectedCategory)?.category || 'Products' : 'All Products';
  return <div className="min-h-screen bg-gray-50 py-6" data-id="element-1610" data-mp="2ixgmzfwv7nxj">
      <div className="max-w-7xl mx-auto px-4" data-id="element-1611" data-mp="2eusbf0gro2wv">
        {/* Breadcrumb */}
        <nav className="text-sm text-gray-500 mb-4" aria-label="Breadcrumb" data-id="element-1612" data-mp="2esz55ma2fkpe">
          <ol className="flex items-center gap-2" data-id="element-1613" data-mp="29uxlp08q9jde">
            <li data-id="element-1614" data-mp="13fn41j5oxg6x">
              <a href="/" className="hover:text-primary-600" data-id="element-1615" data-mp="13g6fqi4qz8ci">
                Home
              </a>
            </li>
            <li data-id="element-1616" data-mp="yfinjt4s8lgp">/</li>
            <li className="text-gray-900 font-medium" data-id="element-1617" data-mp="yez8qlafid6w">{pageTitle}</li>
          </ol>
        </nav>

        {/* Page header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6" data-id="element-1618" data-mp="3a22pfrjbyxw2">
          <div data-id="element-1619" data-mp="3f0mazqu0i7nw">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900" data-id="element-1620" data-mp="3j4u37xzoi5qv">
              {pageTitle}
            </h1>
            <p className="text-gray-600 mt-1" data-id="element-1621" data-mp="3nvtw8s5og3o3">
              {filteredProducts.length} products found
            </p>
          </div>

          <div className="flex items-center gap-3" data-id="element-1622" data-mp="3stvh9imnyd27">
            {/* Mobile filter button */}
            <Button variant="outline" leftIcon={<FilterIcon className="w-4 h-4" data-id="element-1624" data-mp="1px40ga3egvu" />} onClick={() => setShowMobileFilters(true)} className="lg:hidden" data-id="element-1623" data-mp="1mstl161aowq">
              Filters
            </Button>

            {/* Sort dropdown */}
            <div className="w-48" data-id="element-1625" data-mp="amo4d4voghw8">
              <Select options={sortOptions} value={sortBy} onChange={setSortBy} data-id="element-1626" data-mp="28f1uziq0xgo6" />
            </div>
          </div>
        </div>

        <div className="flex gap-6" data-id="element-1627" data-mp="2cgwvwm41ls1f">
          {/* Desktop filters */}
          <aside className="hidden lg:block w-64 flex-shrink-0" data-id="element-1628" data-mp="1h5fi74j17uiz">
            <div className="sticky top-32" data-id="element-1629" data-mp="1h7td9ddpp6qj">
              <ProductFilters selectedCategory={selectedCategory} selectedBrand={selectedBrand} priceRange={priceRange} onCategoryChange={setSelectedCategory} onBrandChange={setSelectedBrand} onPriceRangeChange={setPriceRange} onClearFilters={clearFilters} data-id="element-1630" data-mp="1d555za4nc7vo" />
            </div>
          </aside>

          {/* Product grid */}
          <main className="flex-1" data-id="element-1631" data-mp="yg0ibdf31bg6">
            <ProductGrid products={filteredProducts} columns={3} data-id="element-1632" data-mp="tge87cujwbbl" />

            {filteredProducts.length === 0 && <div className="text-center py-12" data-id="element-1633" data-mp="185ad511b987z">
                <p className="text-gray-500 mb-4" data-id="element-1634" data-mp="18626043iwruk">
                  No products match your filters
                </p>
                <Button variant="outline" onClick={clearFilters} data-id="element-1635" data-mp="13d90vf457hhy">
                  Clear Filters
                </Button>
              </div>}
          </main>
        </div>

        {/* Mobile filters modal */}
        {showMobileFilters && <ProductFilters selectedCategory={selectedCategory} selectedBrand={selectedBrand} priceRange={priceRange} onCategoryChange={setSelectedCategory} onBrandChange={setSelectedBrand} onPriceRangeChange={setPriceRange} onClearFilters={clearFilters} isMobile onClose={() => setShowMobileFilters(false)} data-id="element-1636" data-mp="2mdi4wamgy747" />}
      </div>
    </div>;
}